import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { forumApi } from '../../api/endpoints.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { useNotice } from '../../state/NoticeContext.jsx';
import { Button, Card, Muted, Spinner, fmtDateTime, useAsync } from '../../ui.jsx';

export default function ForumThread() {
  const { id } = useParams();
  const { user } = useAuth();
  const notice = useNotice();
  const { data, loading, error, reload } = useAsync(() => forumApi.detail(id), [id]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [voted, setVoted] = useState(null);

  if (loading) return <Spinner label="Loading discussion" />;
  if (error || !data) {
    return (
      <Card>
        <h2>Discussion not available</h2>
        <Muted>It was not found, or you need to sign in to read it.</Muted>
        <Link to="/forums" className="btn" style={{ textDecoration: 'none' }}>Back to forums</Link>
      </Card>
    );
  }

  const thread = data.thread;

  const reply = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await forumApi.reply(id, { body: body.trim() });
      setBody('');
      notice.success('Reply posted.');
      reload();
    } catch (error) {
      notice.fromError(error);
    } finally {
      setBusy(false);
    }
  };

  const vote = async () => {
    try {
      const result = await forumApi.vote(id, {});
      setVoted(result.voted);
      notice.success(result.voted ? 'Vote counted.' : 'Vote removed.');
      reload();
    } catch (error) {
      notice.fromError(error);
    }
  };

  return (
    <>
      <div className="row sp" style={{ marginBottom: 4 }}>
        <div>
          <h2 style={{ margin: 0 }}>{thread.title}</h2>
          <Muted>
            {thread.category} · {thread.school} · asked {fmtDateTime(thread.createdAt)}
          </Muted>
        </div>
        {user ? (
          <Button size="sm" variant={voted === false ? 's' : undefined} onClick={vote}>
            {thread.votes} {thread.votes === 1 ? 'vote' : 'votes'}
          </Button>
        ) : null}
      </div>

      <Card>
        {(thread.posts || []).map((post) => (
          <div className="prog" key={post.id} style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b>{post.author}</b>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{post.body}</div>
              <div className="mu" style={{ marginTop: 6 }}>{fmtDateTime(post.createdAt)}</div>
            </div>
          </div>
        ))}
      </Card>

      {user ? (
        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Add a reply</h3>
          <form onSubmit={reply}>
            <label>
              <textarea
                rows={4}
                required
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Share what you know, or ask a follow-up question."
              />
            </label>
            <div style={{ marginTop: 12 }}>
              <Button type="submit" busy={busy}>Post reply</Button>
            </div>
          </form>
        </Card>
      ) : (
        <Card>
          <Muted>
            <Link to="/signin">Sign in</Link> to reply.
          </Muted>
        </Card>
      )}

      <div style={{ marginTop: 14 }}>
        <Link to="/forums" className="btn s sm" style={{ textDecoration: 'none' }}>Back to forums</Link>
      </div>
    </>
  );
}
